<template>
  <section id="about" class="py-20 bg-gray-50 dark:bg-gray-900">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <h2 class="section-title" :class="{ 'khmer': isKhmer }">
        {{ $t('about.title') }}
      </h2>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <!-- Profile Image -->
        <div class="relative">
          <div class="relative w-80 h-80 mx-auto lg:mx-0">
            <!-- Profile Image Placeholder -->
            <div class="w-full h-full bg-gradient-to-br from-primary-400 to-purple-500 rounded-2xl shadow-2xl flex items-center justify-center">
              <div class="w-72 h-72 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center">
                <UserIcon class="w-32 h-32 text-gray-400 dark:text-gray-600" />
              </div>
            </div>
            
            <!-- Floating decorative elements -->
            <div class="absolute -top-4 -right-4 w-8 h-8 bg-primary-500 rounded-full animate-float" style="animation-delay: 0s"></div>
            <div class="absolute -bottom-4 -left-4 w-6 h-6 bg-purple-500 rounded-full animate-float" style="animation-delay: 1s"></div>
            <div class="absolute top-1/2 -right-8 w-4 h-4 bg-yellow-400 rounded-full animate-float" style="animation-delay: 2s"></div>
          </div>
        </div>

        <!-- Content -->
        <div class="space-y-6">
          <!-- Main Description -->
          <p class="text-lg text-gray-600 dark:text-gray-300 leading-relaxed" :class="{ 'khmer': isKhmer }">
            {{ $t('about.description') }}
          </p>

          <!-- Tabs for Background and Interests -->
          <div class="mt-8">
            <div class="flex space-x-1 bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
              <button
                @click="activeTab = 'background'"
                :class="[
                  'flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200',
                  activeTab === 'background'
                    ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                ]"
              >
                {{ $t('about.background') }}
              </button>
              <button
                @click="activeTab = 'interests'"
                :class="[
                  'flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200',
                  activeTab === 'interests'
                    ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                ]"
              >
                {{ $t('about.interests') }}
              </button>
            </div>

            <!-- Tab Content -->
            <div class="mt-6">
              <Transition name="fade" mode="out-in">
                <div v-if="activeTab === 'background'" key="background">
                  <p class="text-gray-600 dark:text-gray-300 leading-relaxed" :class="{ 'khmer': isKhmer }">
                    {{ $t('about.backgroundText') }}
                  </p>
                </div>
                <div v-else key="interests">
                  <p class="text-gray-600 dark:text-gray-300 leading-relaxed" :class="{ 'khmer': isKhmer }">
                    {{ $t('about.interestsText') }}
                  </p>
                </div>
              </Transition>
            </div>
          </div>

          <!-- Stats -->
          <div class="grid grid-cols-3 gap-6 mt-8">
            <div class="text-center">
              <div class="text-2xl font-bold text-primary-600 dark:text-primary-400">5+</div>
              <div class="text-sm text-gray-600 dark:text-gray-400">Years Experience</div>
            </div>
            <div class="text-center">
              <div class="text-2xl font-bold text-primary-600 dark:text-primary-400">50+</div>
              <div class="text-sm text-gray-600 dark:text-gray-400">Projects Completed</div>
            </div>
            <div class="text-center">
              <div class="text-2xl font-bold text-primary-600 dark:text-primary-400">20+</div>
              <div class="text-sm text-gray-600 dark:text-gray-400">Happy Clients</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { UserIcon } from '@heroicons/vue/24/outline'

const store = useStore()
const activeTab = ref('background')

const isKhmer = computed(() => store.getters.isKhmer)
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style> 